import { useMemo, useState } from "react";
import ArticleCard from "@/components/base/ArticleCard";
import { latestArticles, topicCategories, type ArticleItem } from "@/mocks/news";

const ALL = "全部";
const categories = [ALL, ...topicCategories.map((item) => item.name)];

export default function LatestFeed() {
  const [activeCategory, setActiveCategory] = useState(ALL);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ArticleItem | null>(null);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return latestArticles.filter((item) => {
      const matchCategory = activeCategory === ALL || item.category === activeCategory;
      const matchKeyword =
        !keyword ||
        item.title.toLowerCase().includes(keyword) ||
        item.excerpt.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, query]);

  return (
    <section id="latest" className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-medium tracking-wide text-accent-700">
              <i className="ri-node-tree" aria-hidden />
              最新爆料 · 按小时更新
            </span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              不用翻旧帖，按分类直接看
            </h2>
          </div>
          <div className="relative w-full md:w-72">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索标题、摘要或标签"
              aria-label="搜索爆料"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((category) => {
            const isActive = activeCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors ${
                  isActive
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                active={selected?.id === article.id}
                onSelect={setSelected}
              />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" aria-hidden />
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">
              这个条件下暂时没有条目
            </p>
            <p className="mt-1.5 text-sm text-foreground-600">
              试试换一个分类，或者清空搜索词
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveCategory(ALL);
                setQuery("");
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}

        {selected && (
          <aside className="mt-8 rounded-lg border border-background-300 bg-background-100 p-5 md:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-900">
                    {selected.category}
                  </span>
                  <span className="rounded bg-primary-100 px-2 py-0.5 font-medium text-primary-800">
                    状态 · {selected.status}
                  </span>
                  <span className="text-foreground-500">{selected.publishedAt}</span>
                </div>
                <h3 className="mt-3 max-w-3xl font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
                  {selected.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="关闭详情"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
              >
                <i className="ri-close-line text-xl" aria-hidden />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
              <div className="md:col-span-2">
                <h4 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                  <i className="ri-history-line text-primary-600" aria-hidden />
                  前情
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{selected.excerpt}</p>
                <p className="mt-3 rounded-md bg-background-50 p-4 text-sm leading-relaxed text-foreground-600">
                  这条线索还在变化中。后续的回应、澄清或反转，都会补充在同一个条目下，
                  不会改写已经发出去的内容，方便你对照前后。
                </p>
              </div>
              <div>
                <h4 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                  <i className="ri-price-tag-3-line text-accent-700" aria-hidden />
                  关注点
                </h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selected.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-secondary-300 bg-secondary-50 px-3 py-1 text-xs text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex flex-col gap-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden />
                    阅读 {selected.reads}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-chat-3-line" aria-hidden />
                    {selected.comments} 条讨论
                  </span>
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>
    </section>
  );
}